import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Layout from "@/components/Layout";
import { useActiveTransferDestinations } from "@/hooks/useTransferDestinations";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Clock, 
  ArrowRight, 
  MapPin, 
  Sparkles, 
  Phone, 
  CheckCircle,
  Car,
  Star,
  Shield,
  Plane
} from "lucide-react";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import strLogo from "@/assets/str-logo.png";

const TransferDestinations = () => {
  const { data: destinations, isLoading } = useActiveTransferDestinations();
  const { getConfig } = useSiteConfig();
  const phoneNumber = getConfig('contact_phone', '+1 (809) 123-4567');

  const features = [
    { icon: Shield, text: "Fully Insured", color: "from-primary to-blue-600" },
    { icon: Clock, text: "24/7 Service", color: "from-secondary to-red-500" },
    { icon: Star, text: "5-Star Rated", color: "from-tropical to-yellow-500" },
    { icon: Plane, text: "Flight Tracking", color: "from-palm to-green-500" }
  ];

  return (
    <Layout>
      <Helmet>
        <title>Transfer Destinations | Servicio Turístico Rachel - Dominican Republic</title>
        <meta name="description" content="Discover our transfer destinations across Dominican Republic. Premium airport transfers, competitive prices, and 24/7 availability with STR." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-tropical"></div>
        
        {/* Animated Blobs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-palm/30 rounded-full blur-3xl animate-blob"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-tropical/20 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-blob animation-delay-4000"></div>
        
        {/* Floating Icons */}
        <div className="absolute top-1/4 left-1/4 animate-float">
          <MapPin className="w-10 h-10 text-white/30" />
        </div>
        <div className="absolute bottom-1/3 right-1/4 animate-float animation-delay-2000">
          <Car className="w-8 h-8 text-tropical/50" />
        </div>
        <div className="absolute top-1/3 right-1/3 animate-float animation-delay-4000">
          <Plane className="w-6 h-6 text-white/40" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            {/* Logo */}
            <div className="mb-8 flex justify-center animate-fade-in-up">
              <img 
                src={strLogo} 
                alt="Servicio Turístico Rachel" 
                className="h-20 w-auto drop-shadow-2xl"
              />
            </div>

            <Badge className="mb-6 bg-white/20 backdrop-blur-sm text-white border-white/30 text-lg py-2 px-6 animate-fade-in-up animation-delay-200">
              <MapPin className="w-4 h-4 mr-2" />
              Transfer Destinations
            </Badge>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in-up animation-delay-400">
              <span className="block">Explore Our</span>
              <span className="bg-gradient-to-r from-tropical via-white to-palm bg-clip-text text-transparent">
                Destinations
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/90 leading-relaxed max-w-3xl mx-auto mb-10 animate-fade-in-up animation-delay-600">
              Premium airport transfers and transportation to the most beautiful 
              destinations across Dominican Republic.
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap justify-center gap-3 animate-fade-in-up animation-delay-800">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full"
                >
                  <feature.icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="hsl(var(--background))"/>
          </svg>
        </div>
      </section>

      {/* Destinations Grid */}
      <section className="py-20 bg-background relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16 animate-fade-in-up">
            <Badge className="mb-6 bg-gradient-to-r from-primary to-secondary text-white border-0 text-base py-2 px-6">
              <Sparkles className="w-4 h-4 mr-2" />
              Popular Routes
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-foreground">Where Would You</span>
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> Like to Go?</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Select your destination and book your premium transfer in minutes
            </p>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-20">
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                <p className="text-muted-foreground">Loading destinations...</p>
              </div>
            </div>
          ) : destinations && destinations.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {destinations.map((destination, index) => (
                <Card 
                  key={destination.id} 
                  className="group relative overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 animate-fade-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Image Section */}
                  {destination.hero_image && (
                    <div className="relative h-56 overflow-hidden">
                      <img 
                        src={destination.hero_image} 
                        alt={destination.name} 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      
                      {/* Price Badge */}
                      {destination.from_airport_price && (
                        <Badge className="absolute top-4 right-4 bg-gradient-to-r from-tropical to-palm text-white border-0 font-bold text-lg px-4 py-2 shadow-xl">
                          From ${destination.from_airport_price}
                        </Badge>
                      )}
                      
                      {/* Destination Name Overlay */}
                      <div className="absolute bottom-4 left-4 right-4">
                        <h2 className="text-2xl font-bold text-white drop-shadow-lg">{destination.name}</h2>
                        {destination.estimated_time && (
                          <div className="flex items-center gap-2 text-white/90 mt-1">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm">{destination.estimated_time} from airport</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <CardContent className="p-6">
                    {/* Description */}
                    {destination.description && (
                      <p className="text-muted-foreground mb-4 line-clamp-2">
                        {destination.description}
                      </p>
                    )}
                    
                    {/* Highlights */}
                    {destination.highlights && destination.highlights.length > 0 && (
                      <ul className="space-y-2 mb-6">
                        {destination.highlights.slice(0, 3).map((highlight, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <CheckCircle className="w-4 h-4 text-palm flex-shrink-0 mt-0.5" />
                            <span>{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    
                    {/* CTA Button */}
                    <Link to={`/traslados/${destination.slug}`}>
                      <Button className="w-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white shadow-lg group/btn">
                        View Details & Book
                        <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-0 shadow-lg max-w-md mx-auto">
              <CardContent className="text-center py-16">
                <div className="w-20 h-20 mx-auto bg-muted rounded-full flex items-center justify-center mb-6">
                  <MapPin className="w-10 h-10 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-bold mb-2">No Destinations Available</h3>
                <p className="text-muted-foreground mb-6">
                  We're updating our destinations. Please check back soon or contact us directly.
                </p>
                <Link to="/contact">
                  <Button>Contact Us</Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary via-primary to-tropical"></div>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-10 left-10 animate-float">
          <Sparkles className="w-12 h-12 text-white/30" />
        </div>
        <div className="absolute bottom-10 right-10 animate-float animation-delay-2000">
          <MapPin className="w-10 h-10 text-tropical/50" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in-up">
              Can't Find Your
              <span className="block text-tropical">Destination?</span>
            </h2>
            <p className="text-xl md:text-2xl mb-10 opacity-90 animate-fade-in-up animation-delay-200">
              Contact our team and we'll organize your transfer to any location 
              in Dominican Republic. Custom routes, group transfers, and special requests welcome!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-400">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-primary hover:bg-white/90 text-lg px-10 py-6 shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
                  <Sparkles className="mr-2 h-5 w-5" />
                  Contact Us Now
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-primary text-lg px-10 py-6 hover:scale-105 transition-all"
                onClick={() => window.open(`tel:${phoneNumber}`, '_self')}
              >
                <Phone className="mr-2 h-5 w-5" />
                Call Us Direct
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TransferDestinations;
